
import type { PropType } from 'vue'
import Sortable from 'sortablejs'
import { defineComponent } from 'vue'
import { EpbButton } from './shared/epb-button'
import TransitionSize from './transition-size.component.vue'

export interface MutableListItem {
  id: string
  editable?: boolean
  removable?: boolean
  [key: string]: any
}

export interface MutableListStyle {
  contentGap?: string
  itemPadding?: string
}

export default defineComponent({
  name: 'MutableList',
  components: {
    EpbButton,
    TransitionSize,
  },
  props: {
    items: {
      type: Array as PropType<Array<MutableListItem>>,
      default: () => [],
    },
    direction: {
      type: String as PropType<'column' | 'row'>,
      default: () => 'column',
    },
    sortable: Boolean,
    sortGroupField: String,
    editable: Boolean,
    editedItemId: String,
    expandible: Boolean,
    separated: {
      type: Boolean,
      default: true,
    },
    forceFallback: Boolean,
    expandedItemIds: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    removable: Boolean,
    disabledItemsIds: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    styles: {
      type: Object as PropType<MutableListStyle>,
      default: () => ({
        itemPadding: '12px 0',
      }),
    },
  },
  data() {
    return {
      sortableListObject: undefined,
    }
  },
  computed: {
    buttonProps() {
      return {
        type: 'void',
        size: 'small',
      }
    },
    itemClasses(): (string | Record<string, boolean>)[] {
      return [
        'epb-mutable-list__item',
        {
          'epb-mutable-list__item--separated': this.separated,
        },
      ]
    },
  },
  mounted(): void {
    if (this.sortable) {
      this.sortableListObject = Sortable.create(this.$refs.list.$el, {
        forceFallback: this.forceFallback,
        handle: '.epb-mutable-list__button--sort',
        ghostClass: 'epb-mutable-list__item--ghost',
        filter: '.epb-mutable-list__button:disabled',
        animation: 500,
        ...(!!this.sortGroupField && {
          onMove: event => {
            const draggedItem = this.items[Sortable.utils.index(event.dragged, '>*')]
            const targetItem = this.items[Sortable.utils.index(event.related, '>*')]
            return draggedItem[this.sortGroupField] === targetItem[this.sortGroupField]
          },
        }),
        onUpdate: event => {
          this.$emit('moveItem', {
            item: this.items[event.oldIndex],
            targetIndex: event.newIndex,
          })
        },
      })
    }
  },
  beforeDestroy(): void {
    if (this.sortableListObject) {
      this.sortableListObject.destroy()
    }
  },
  methods: {
    isItemDisabled(itemId: string): boolean {
      return this.disabledItemsIds.includes(itemId)
    },
    isItemEditable(item: MutableListItem): boolean {
      if (this.editable) {
        return item.editable !== undefined ? item.editable : true
      }
      return !!item.editable
    },
    isItemIdEdited(itemId: string): boolean {
      return itemId === this.editedItemId
    },
    isItemRemovable(item: MutableListItem): boolean {
      if (this.removable) {
        return item.removable !== undefined ? item.removable : true
      }
      return !!item.removable
    },
    isItemIdExpanded(itemId: string): boolean {
      return this.expandedItemIds.includes(itemId)
    },
  },
})
